<template>
  <div>
    <v-row>
      <v-col lg="4" md="4" cols="12">
        <v-autocomplete
          outlined
          :label="lblMembershipType"
          deletable-chips
          small-chips
          v-model="membershipType"
          :items="filteredMembershipTypes"
          item-text="name"
          item-value="id"
          :return-object="true"
          class="mt-3"
          :no-data-text="$t('common.no_data')"
        ></v-autocomplete>
      </v-col>
    </v-row>
    <div v-if="selectedEventHasDailyEvents">
      <v-row>
        <v-col lg="12" md="12" cols="12">
          <div class="text-subtitle-1 font-weight-medium">
            {{ $t('memberRegistration.step3.content.event_for_participant') }}
          </div>
        </v-col>
      </v-row>

      <v-row v-for="participant in participants" :key="participant.id">
        <v-col cols="12">
          <v-row >
            <v-col cols="2" sm="2" class="pr-0">
              <div class="text-subtitle-1 font-weight-medium">
                {{ participant.fName }} {{ participant.lName }}
              </div>
            </v-col>
            <v-col cols="6" sm="6">
              <v-autocomplete
                :label="$t('memberRegistration.step3.content.select_event_for_participant')"
                :items="eventsAvailableForParticipant(participant)"
                item-text="eventName"
                v-model="participant.membershipEventId"
                item-value="id"
                :no-data-text="$t('common.no_data')"
                outlined
                deletable-chips
                small-chips
                hide-details
              ></v-autocomplete>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
    <v-row v-if="membershipType">
      <v-col lg="12" md="12" cols="12">
        <v-card class="mx-auto" v-if="fullMembershipType">
          <v-card-title>
            {{ fullMembershipType.Name }}
          </v-card-title>

          <v-card-subtitle>
            <b>{{ fullMembershipType.Description }}</b>
          </v-card-subtitle>

          <v-card-text v-if="!dailyEvent">
            <div v-if="fullMembershipType.periodType == 1">
              <v-card-subtitle class="px-0">
                {{ $t("memberRegistration.prices.membership_valid_for_one_event") }}
              </v-card-subtitle>
              <v-card-subtitle class="px-0" v-if="formatMembershipEvents(fullMembershipType)">
                {{ $t("memberRegistration.prices.available_events") }} {{ formatMembershipEvents(fullMembershipType) }}
              </v-card-subtitle>
            </div>
            <div v-else>
              <v-card-subtitle class="membership-meta-block pb-0 px-0">
                <p>
                  <span class="membership-meta-label">{{ $t("memberRegistration.prices.pre_registration_for_existing_members_only") }}</span>
                  {{ fullMembershipType.preRegFrom | formatDateForHumans(false) }} {{ $t("memberRegistration.prices.to") }} {{ fullMembershipType.preRegTo | formatDateForHumans(false) }}
                </p>
                <p>
                  <span class="membership-meta-label">{{ $t("memberRegistration.prices.membership_range") }}</span>
                  {{ fullMembershipType.dateFrom | formatDateForHumans(false) }} {{ $t("memberRegistration.prices.to") }} {{ fullMembershipType.dateTo | formatDateForHumans(false) }}
                </p>
              </v-card-subtitle>
              <v-card-subtitle class="membership-validity-block pt-0 px-0">
                <h2 class="membership-validity-title">
                  {{ $t("memberRegistration.prices.your_membership_is_only_valid_until") }}
                  {{ fullMembershipType.dateTo | formatDateForHumans(false) }}
                </h2>
                <p class="membership-validity-note">
                  ({{ $t("memberRegistration.prices.it-is-not-valid-for-12-consecutive-months") }} {{ fullMembershipType.dateTo | formatDateForHumans(false) }})
                </p>
              </v-card-subtitle>
            </div>
            <br />
            <div class="text-subtitle-1 font-weight-medium">
              {{ $t("memberRegistration.step3.content.membership_range") }}
            </div>
            <div>
              {{ this.$options.filters.formatDateForHumans(fullMembershipType.dateFrom, false) }}
              {{ $t("memberRegistration.step3.content.to") }}
              {{ this.$options.filters.formatDateForHumans(fullMembershipType.dateTo, false) }}
            </div>
            <br />
            <div class="text-subtitle-1 font-weight-medium text-danger">
              {{ $t("memberRegistration.step3.content.valid_until") }}
              <span class="text-bold" style="font-size:20px;">{{
                this.$options.filters.formatDateForHumans(fullMembershipType.dateTo, false)
              }}</span>
            </div>
            <div>
              ({{
                $t(
                  "memberRegistration.step3.content.it_is_not_valid_for_12_consec_months"
                )
              }}
              {{ this.$options.filters.formatDateForHumans(fullMembershipType.dateTo, false) }})
            </div>

            <div class="pricing-preview-card">
              <table class="billingDetails membership-pricing-table">
                <tr>
                  <td>
                    {{
                      fullMembershipType.periodType == 1
                        ? $t("memberRegistration.prices.membership_fee_for_date")
                        : $t("memberRegistration.prices.membership_fee_for")
                    }}
                  </td>
                  <td class="orgprice">{{ formatPrice(fullMembershipType.priceTaxed) }}</td>
                </tr>
                <tr v-for="tax in fullMembershipType.taxes" :key="tax.id || `${tax.taxLabel}-${tax.taxNumber}`">
                  <td>{{ formatMembershipTaxLabel(tax) }}</td>
                  <td class="orgprice">{{ formatMembershipTaxAmount(tax) }}</td>
                </tr>
                <tr class="totalline membership-total-row">
                  <td>
                    <h3>{{ getMembershipTotalLabel(fullMembershipType.currency) }}</h3>
                  </td>
                  <td class="orgprice">
                    <h3>{{ formatPrice(fullMembershipType.price) }}</h3>
                  </td>
                </tr>
              </table>
              <p class="membership-pricing-note">{{ $t("memberRegistration.prices.additional_class_fees_note") }}</p>
            </div>

            <v-card elevation="0" v-if="fullMembershipType.rules">
              <v-card-title>
                {{ $t("memberRegistration.step3.content.member_type_rules") }}
              </v-card-title>
              <v-card-text>
                <v-simple-table>
                  <template v-slot:default>
                    <tbody>
                      <tr>
                        <td>
                          {{
                            $t(
                              "memberRegistration.step3.content.member_type_rules_ages"
                            )
                          }}
                        </td>
                        <td>
                          {{
                            $t(
                              "memberRegistration.step3.content.member_type_rules_fee"
                            )
                          }}
                        </td>
                        <td>
                          {{
                            $t(
                              "memberRegistration.step3.content.member_type_rules_charge"
                            )
                          }}
                        </td>
                      </tr>
                      <tr
                        v-for="rule in fullMembershipType.rules"
                        :key="rule.id"
                      >
                        <td>
                          {{ rule.ageMin }}
                          {{ $t("memberRegistration.step3.content.to") }}
                          {{ rule.ageMax }}
                        </td>
                        <td>
                          {{ $t("common.currency.left") }} {{ rule.fee }}
                          {{ $t("common.currency.right") }}
                        </td>
                        <td>
                          {{
                            rule.IsOneTimeFee
                              ? $t(
                                  "memberRegistration.step3.content.member_type_rules_one"
                                )
                              : $t(
                                  "memberRegistration.step3.content.member_type_rules_multiple"
                                )
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-card-text>

          <v-card-text v-else>
            <div v-if="dailyEvent">
              <div class="text-subtitle-1 font-weight-medium">
                {{ $t("memberRegistration.step3.content.membership_range") }}
              </div>
              <div>
                {{ fullMembershipType.preRegFrom }}
                {{ $t("memberRegistration.step3.content.to") }}
                {{ fullMembershipType.dateTo }}
              </div>
              <br />
              <div class="text-subtitle-1 font-weight-medium text-danger">
                {{ $t("memberRegistration.step3.content.valid_until") }}
                <span class="text-bold" style="font-size:20px;">{{
                  this.$options.filters.formatDateForHumans(fullMembershipType.dateTo, false)
                }}</span>
              </div>
              <div>
                ({{
                  $t(
                    "memberRegistration.step3.content.membership_only_valid_for_event"
                  )
                }}
                {{ dailyEvent.eventName }})
              </div>

              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td>{{ $t("memberRegistration.step3.content.membership") }}</td>
                      <td>
                        {{ $t("common.currency.left") }}
                        {{
                          fullMembershipType.taxable
                            ? fullMembershipType.priceWithoutTaxes
                            : fullMembershipType.price
                        }}
                        {{ $t("common.currency.right") }}
                      </td>
                    </tr>
                    <tr
                      v-for="(tax, index) in fullMembershipType.taxes"
                      :key="index"
                    >
                      <td>
                        {{ $t("memberRegistration.step6.content.tax") }} {{ tax.taxLabel }}<small>({{ tax.taxNumber }})</small>
                      </td>
                      <td>
                        {{ $t("common.currency.left") }} {{ tax.taxAmount }}
                        {{ $t("common.currency.right") }}
                      </td>
                    </tr>
                    <tr class="font-weight-medium">
                      <td>
                        {{
                          $t(
                            "memberRegistration.step3.content.membership_fee_for"
                          )
                        }}
                        {{
                          new Date(fullMembershipType.dateFrom).getFullYear()
                        }}
                        ({{ fullMembershipType.currency }})
                      </td>
                      <td>
                        {{ $t("common.currency.left") }}
                        {{ fullMembershipType.price }}
                        {{ $t("common.currency.right") }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
            <v-card elevation="0" v-if="fullMembershipType.rules">
              <v-card-title>
                {{ $t("memberRegistration.step3.content.member_type_rules") }}
              </v-card-title>
              <v-card-text>
                <v-simple-table>
                  <template v-slot:default>
                    <tbody>
                      <tr>
                        <td>
                          {{
                            $t(
                              "memberRegistration.step3.content.member_type_rules_ages"
                            )
                          }}
                        </td>
                        <td>
                          {{
                            $t(
                              "memberRegistration.step3.content.member_type_rules_fee"
                            )
                          }}
                        </td>
                        <td>
                          {{
                            $t(
                              "memberRegistration.step3.content.member_type_rules_charge"
                            )
                          }}
                        </td>
                      </tr>
                      <tr
                        v-for="rule in fullMembershipType.rules"
                        :key="rule.id"
                      >
                        <td>
                          {{ rule.ageMin }}
                          {{ $t("memberRegistration.step3.content.to") }}
                          {{ rule.ageMax }}
                        </td>
                        <td>{{ rule.fee }}</td>
                        <td>
                          {{
                            rule.IsOneTimeFee
                              ? $t(
                                  "memberRegistration.step3.content.member_type_rules_one"
                                )
                              : $t(
                                  "memberRegistration.step3.content.member_type_rules_multiple"
                                )
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
        </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" sm="6" cols="6" class="text-left">
        <v-btn
          :color="`${buttonColor}`"
          class="btn_next"
          v-model="step"
          @click="nextStep(stepBack)"
          >{{ $t("registerEvent.prev") }}</v-btn
        >
      </v-col>
      <v-col md="6" sm="6" cols="6" class="text-right">
        <v-btn
          :color="`${buttonColor}`"
          class="btn_next"
          :disabled="membershipType == null"
          v-model="step"
          @click="nextStep(stepNext)"
          >{{ $t("registerEvent.next") }}</v-btn
        >
      </v-col>
    </v-row>
    <Snack :snack="snackbar" :snackMsg="msg_snackbar" :snackCol="color_snackbar"/>
  </div>
</template>

<script>
import Snack from "@/components/notifications/Snack";
import { isArray } from "lodash";
import {
  VRow,
  VCol,
  VBtn,
  VAutocomplete,
  VCard,
  VCardText,
  VCardTitle,
  VCardSubtitle,
  VSimpleTable,
} from "vuetify/lib";

export default {
  components: {
    VRow,
    VCol,
    VBtn,
    VAutocomplete,
    VCard,
    VCardText,
    VCardTitle,
    VCardSubtitle,
    VSimpleTable,
    Snack
  },
  props: {
    step: Number,
    membershipTypes: Array,
    itemOrg: Object,
    buttonColor: String,
    previousStep: Number,
    participants: Array
  },
  data() {
    return {
      key: 0,
      dailyEvent: null,
      membershipType: null,
      isHeadshotRequired: false,
      isTeamRequired: false,
      isRequiredMemberRelation: false,
      msg_snackbar: '',
      color_snackbar: '',
      snackbar: false,
    };
  },
  computed: {
    selectedEventDefault(){
      if(this.fullMembershipType && this.fullMembershipType.events && this.fullMembershipType.events.length === 1){
        return this.fullMembershipType.events[0].id;
      }
      return null;
    },
    stepBack() {
      return this.previousStep;
    },
    stepNext() {
      if (this.isHeadshotRequired) {
        return 3.1;
      } else if (this.isTeamRequired) {
        return 3.2;
      } else if (this.isRequiredMemberRelation) {
        return 3.3;
      } else if (this.skipClassSelection === 1) {
        return this.step + 2;
      } else {
        return this.step + 1;
      }
    },
    lblMembershipType() {
      return this.membershipType
        ? this.$t(
            "memberRegistration.step3.content.membershipType_selected_lbl"
          )
        : this.$t("memberRegistration.step3.content.membershipType_lbl");
    },
    lblDailyEvent() {
      return this.dailyEvent
        ? this.$t("memberRegistration.step3.content.daily_event_selected_lbl")
        : this.$t("memberRegistration.step3.content.daily_event_lbl");
    },
    filteredMembershipTypes() {
      return this.membershipTypes.map((element) => ({
        id: element.id,
        name: element.Name,
      }));
    },
    fullMembershipType() {
      return this.membershipType
        ? this.membershipTypes.find(
            (item) => item.id === this.membershipType.id
          )
        : null;
    },
    skipClassSelection() {
      return this.fullMembershipType.isClassFree == 1;
    },
    selectedEventHasDailyEvents() {
      return this.membershipType && this.fullMembershipType.periodType == 1;
    },
  },
  methods: {
    formatPrice(value, freeWhenZero = false) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        if (numericValue === 0) {
          return freeWhenZero ? this.$t("registerEvent.membership_free") : "0";
        }
        const rawValue = typeof value === "string" ? value.trim() : numericValue.toString();
        return `${this.$t("common.currency.left")}${rawValue}${this.$t("common.currency.right")}`;
      }

      const rawValue = String(value).trim();
      return rawValue
        ? `${this.$t("common.currency.left")}${rawValue}${this.$t("common.currency.right")}`
        : "";
    },
    formatMembershipEvents(membershipType) {
      if (!membershipType || !Array.isArray(membershipType.events)) {
        return "";
      }

      return membershipType.events
        .map((event) => event.eventName)
        .filter(Boolean)
        .join(", ");
    },
    formatMembershipTaxLabel(tax) {
      if (!tax) {
        return "";
      }

      const label = (tax.taxLabel || "").trim();
      const number = tax.taxNumber ? ` (${tax.taxNumber})` : "";
      const source = String(tax.source || "").toLowerCase();
      const isIncluded = source === "included";
      const sign = isIncluded ? "=" : "+";
      return `${sign} ${label}${number}`;
    },
    getMembershipTotalLabel(currencyCode) {
      const code = String(currencyCode || "USD").toUpperCase();
      return `${this.$t("memberRegistration.prices.membership_total", { currency: code })}`;
    },
    formatMembershipTaxAmount(tax) {
      if (!tax) {
        return "";
      }

      const amount = this.formatPrice(tax.taxAmount);
      const source = String(tax.source || "").toLowerCase();
      const includedSuffix = source === "included" ? " (included)" : "";
      return `${amount}${includedSuffix}`;
    },
    existingMembershipEventIds(participant) {
      return (participant && Array.isArray(participant.existingMembershipEventIds) ? participant.existingMembershipEventIds : [])
        .map((eventId) => Number(eventId))
        .filter((eventId) => Number.isFinite(eventId) && eventId > 0);
    },
    eventsAvailableForParticipant(participant) {
      const events = this.fullMembershipType && Array.isArray(this.fullMembershipType.events)
        ? this.fullMembershipType.events
        : [];
      const existingEventIds = this.existingMembershipEventIds(participant);
      return events.filter((event) => !existingEventIds.includes(Number(event.id)));
    },
    participantHasSelectedBlockedEvent(participant) {
      if (!participant || !participant.membershipEventId) {
        return false;
      }

      return this.existingMembershipEventIds(participant).includes(Number(participant.membershipEventId));
    },
    syncParticipantEventSelections() {
      if (!this.selectedEventHasDailyEvents || !Array.isArray(this.participants)) {
        return;
      }

      this.participants.forEach((participant) => {
        if (this.participantHasSelectedBlockedEvent(participant)) {
          participant.membershipEventId = null;
        }

        const availableEvents = this.eventsAvailableForParticipant(participant);
        if (!participant.membershipEventId && availableEvents.length === 1) {
          participant.membershipEventId = availableEvents[0].id;
        }
      });
    },
    showSnackBar(msg, color){
      this.snackbar = true;
      this.msg_snackbar = msg;
      this.color_snackbar = color;
      setTimeout(() => this.snackbar = false, 3000);
    },
    nextStep(step) {
      if(this.selectedEventHasDailyEvents){
        const participantsWithSelectedEvent = this.participants.filter(participant => participant.membershipEventId && !this.participantHasSelectedBlockedEvent(participant));
        if(participantsWithSelectedEvent.length === this.participants.length){
          this.$emit("nextStep", step);
        } else {
          this.showSnackBar(this.$t("memberRegistration.step3.content.error_selected_event_for_all_participants"), "error");
        }
      } else {
        this.$emit("nextStep", step);
      }
    }
  },
  watch: {
    membershipType(val) {
      if (!val) {
        this.$emit("setSkipClassSelection", false);
        return;
      }
      this.isHeadshotRequired = this.fullMembershipType.isHeadshotRequired;
      this.isTeamRequired = !!this.fullMembershipType.isTeamRequired;
      this.isRequiredMemberRelation = !!this.fullMembershipType.isRequiredMemberRelation;
      this.$emit("setSkipClassSelection", this.skipClassSelection);
      this.$emit("setMembershipType", this.fullMembershipType);
      if (
        this.fullMembershipType.periodType == 1 &&
        this.fullMembershipType.events.length == 1
      ) {
        this.dailyEvent = this.fullMembershipType.events[0];
      }
      else
      {
        this.dailyEvent = null;
      }
      this.$nextTick(this.syncParticipantEventSelections);
    },
    selectedEventDefault(val) {
      if (val && this.participants) {
        this.syncParticipantEventSelections();
      }
    },
    filteredMembershipTypes(val) {
      this.membershipType = null;
      if (isArray(val) && val.length == 1) this.membershipType = val[0];
    },
    dailyEvent(val)
    {
      this.$emit("updateDailyEvent", val);
    },
    participants: {
      handler(newParticipants) {
        this.syncParticipantEventSelections();
        this.$emit("updateParticipants", newParticipants);
      },
      deep: true
    }
  }
};
</script>
